/**
 * OrgToolbar - Controls for org chart view switching and navigation
 */

import React, { useState } from 'react';
import { FILTER_KEYS, FILTER_COLORS, FILTER_PRIORITY } from '../../utils/filters';

const VIEW_OPTIONS = [
  { id: 'baseshop', label: 'BaseShop', description: 'Direct organization (excludes promoted brokers)' },
  { id: 'superbase', label: 'SuperBase', description: 'Your promoted brokers' },
  { id: 'superteam', label: 'SuperTeam', description: 'Full organization including all brokers' }
];

const FILTER_OPTIONS = [
  { key: FILTER_KEYS.BPM, label: 'BPM Attendance', color: FILTER_COLORS[FILTER_KEYS.BPM] },
  { key: FILTER_KEYS.BIG_EVENT, label: 'Big Event', color: FILTER_COLORS[FILTER_KEYS.BIG_EVENT] },
  { key: FILTER_KEYS.KEY_PLAYER, label: 'Key Player', color: FILTER_COLORS[FILTER_KEYS.KEY_PLAYER] },
  { key: FILTER_KEYS.LICENSED, label: 'Licensed', color: FILTER_COLORS[FILTER_KEYS.LICENSED] },
  { key: FILTER_KEYS.NET_LICENSED, label: 'Net Licensed', color: FILTER_COLORS[FILTER_KEYS.NET_LICENSED] },
  { key: FILTER_KEYS.CLIENT, label: 'Client', color: FILTER_COLORS[FILTER_KEYS.CLIENT] },
];

function OrgToolbar({
  currentView = 'baseshop',
  onViewChange,
  onSearch,
  onCenterOnMe,
  onSelectBroker,
  activeFilters = new Set(),
  onFilterToggle,
  searchEnabled = true,
  centerOnMeEnabled = true,
  selectBrokerEnabled = false,
  // SMD selection for SuperBase/SuperTeam views
  smdList = [],
  selectedSMDId = null,
  onSMDSelect,
  // Expand/Collapse controls
  onExpandAll,
  onCollapseAll,
  onExpandToDepth,
  expandDepth = null,
  showExpandControls = false,
  // Refresh control
  onRefresh,
  // User list for autocomplete
  users = []
}) {
  const [searchValue, setSearchValue] = useState('');
  const [showDepthMenu, setShowDepthMenu] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim() && onSearch) {
      onSearch(searchValue.trim());
      setShowAutocomplete(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Filter users based on search input
    if (value.trim().length >= 2) {
      const query = value.toLowerCase();
      const filtered = users.filter(user =>
        (user.name || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query) ||
        (user.plan || '').toLowerCase().includes(query)
      ).slice(0, 8); // Limit to 8 results
      
      setFilteredUsers(filtered);
      setShowAutocomplete(filtered.length > 0);
    } else {
      setFilteredUsers([]);
      setShowAutocomplete(false);
    }
  };

  const handleUserSelect = (user) => {
    if (onSearch) {
      onSearch(user.name);
    }
    setSearchValue(user.name);
    setShowAutocomplete(false);
  };

  const handleCenterOnMe = () => {
    if (onCenterOnMe) {
      onCenterOnMe();
    }
  };

  const handleDepthSelect = (depth) => {
    if (onExpandToDepth) {
      onExpandToDepth(depth === 'all' ? null : depth);
    }
    setShowDepthMenu(false);
    // Recenter after changing depth so the main user stays in view
    handleCenterOnMe();
  };

  return (
    <div className="org-toolbar">
      {/* View Switcher */}
      <div className="org-toolbar-section">
        <label className="org-toolbar-label">View:</label>
        <div className="org-toolbar-views">
          {VIEW_OPTIONS.map(view => (
            <button
              key={view.id}
              className={`org-toolbar-view-btn ${currentView === view.id ? 'active' : ''}`}
              onClick={() => onViewChange && onViewChange(view.id)}
              title={view.description}
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>


      {/* Team Dropdown with SMD Names - Only for SuperBase/SuperTeam views */}
      {(currentView === 'superbase' || currentView === 'superteam') && smdList.length > 0 && (
        <div className="org-toolbar-section">
          <label className="org-toolbar-label">Team:</label>
          <select
            className="org-toolbar-team-selector"
            value={selectedSMDId || ''}
            onChange={(e) => onSMDSelect && onSMDSelect(e.target.value || null)}
          >
            <option value="">Select Team...</option>
            {smdList.map(smd => (
              <option key={smd.id} value={smd.id}>
                {smd.firstName} {smd.lastName} ({smd.agentLevel})
              </option>
            ))}
          </select>
        </div>
      )}


      {/* Expand/Collapse Controls */}
      {showExpandControls && (
        <div className="org-toolbar-section org-toolbar-expand-controls">
          <label className="org-toolbar-label">Expand:</label>
          <div className="org-toolbar-expand-btns">
            {/* <button
              className="org-toolbar-expand-btn"
              onClick={onExpandAll}
              title="Expand all nodes"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
                <path d="M3 8h10M8 3v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              All
            </button>
            <button
              className="org-toolbar-expand-btn"
              onClick={onCollapseAll}
              title="Collapse all nodes"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
                <path d="M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Collapse
            </button> */}
            <div className="org-toolbar-depth-selector">
              <button
                className="org-toolbar-expand-btn org-toolbar-depth-btn"
                onClick={() => setShowDepthMenu(!showDepthMenu)}
                title="Expand to specific depth"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px' }}>
                  <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Depth {expandDepth !== null ? expandDepth : '∞'}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: '4px' }}>
                  <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {showDepthMenu && (
                <div className="org-toolbar-depth-menu">
                  <button onClick={() => handleDepthSelect('all')} className={expandDepth === null ? 'active' : ''}>
                    All Levels
                  </button>
                  <button onClick={() => handleDepthSelect(1)} className={expandDepth === 1 ? 'active' : ''}>
                    Level 1
                  </button>
                  <button onClick={() => handleDepthSelect(2)} className={expandDepth === 2 ? 'active' : ''}>
                    Level 2
                  </button>
                  <button onClick={() => handleDepthSelect(3)} className={expandDepth === 3 ? 'active' : ''}>
                    Level 3
                  </button>
                  <button onClick={() => handleDepthSelect(4)} className={expandDepth === 4 ? 'active' : ''}>
                    Level 4
                  </button>
                  <button onClick={() => handleDepthSelect(5)} className={expandDepth === 5 ? 'active' : ''}>
                    Level 5
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter Pills */}
      <div className="org-toolbar-section org-toolbar-filters">
        <label className="org-toolbar-label">Filters:</label>
        <div className="org-toolbar-filter-pills">
          {FILTER_OPTIONS.map(filter => {
            const isActive = activeFilters.has(filter.key);
            return (
              <button
                key={filter.key}
                className={`org-filter-pill ${isActive ? 'active' : ''}`}
                style={{
                  '--filter-color': filter.color,
                  backgroundColor: isActive ? filter.color : 'transparent',
                  borderColor: filter.color,
                  color: isActive ? '#fff' : filter.color
                }}
                onClick={() => onFilterToggle && onFilterToggle(filter.key)}
                title={`Toggle ${filter.label} filter`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search with Autocomplete */}
      {searchEnabled && (
        <div className="org-toolbar-section">
          <form onSubmit={handleSearchSubmit} className="org-toolbar-search">
            <div className="org-toolbar-search-wrapper">
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (filteredUsers.length > 0) {
                    setShowAutocomplete(true);
                  }
                }}
                onBlur={() => {
                  // Delay to allow click on dropdown items
                  setTimeout(() => setShowAutocomplete(false), 200);
                }}
                placeholder="Search by name..."
                className="org-toolbar-search-input"
                autoComplete="off"
              />
              {showAutocomplete && filteredUsers.length > 0 && (
                <div className="org-toolbar-autocomplete">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="org-toolbar-autocomplete-item"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur
                        handleUserSelect(user);
                      }}
                    >
                      <div className="org-toolbar-autocomplete-name">{user.name}</div>
                      <div className="org-toolbar-autocomplete-meta">
                        <span className="org-toolbar-autocomplete-type">{user.plan || 'Agent'}</span>
                        {user.email && <span className="org-toolbar-autocomplete-email">{user.email}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* <button type="submit" className="org-toolbar-search-btn">
              Search
            </button> */}
          </form>
        </div>
      )}

      {/* Center on Me */}
      {centerOnMeEnabled && (
        <div className="org-toolbar-section">
          <button
            className="org-toolbar-btn"
            onClick={handleCenterOnMe}
          >
            Center on Me
          </button>
        </div>
      )}

      {/* Refresh Data */}
      {onRefresh && (
        <div className="org-toolbar-section">
          <button
            className="org-toolbar-btn"
            onClick={onRefresh}
            title="Clear cache and reload data"
          >
            🔄 Refresh
          </button>
        </div>
      )}

      {/* Select Broker */}
      {selectBrokerEnabled && (
        <div className="org-toolbar-section">
          <button
            className="org-toolbar-btn org-toolbar-btn-primary"
            onClick={onSelectBroker}
          >
            Select Different Broker
          </button>
        </div>
      )}
    </div>
  );
}

export default OrgToolbar;

